import axios from 'axios';
import React, { useState } from 'react';
import { isAdmin } from '../../utils/permissions';

// Cap the raw file so the base64 stays comfortably under the server's limit
// (The API rejects filess over ~750 KB, which is way larger than a license file).
const MAX_LICENSE_BYTES = 5 * 1024;

const LicenseUploadModal = ({ onClose }) => {

  const [licenseData, setLicenseData] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const admin = isAdmin();

  // Compare against what was loaded, so Save only lights up on a real change.
  const hasChanges =
    (licenseData ?? '') !== ('');

  const onLicenseFile = (e) => {
    setError('');
    setMsg('');

    const file = e.target.files?.[0];

    if (!file) return;
    if (!file.type === ('text/plain')) {
      return setError('Please choose a text file (TXT).');
    }
    if (file.size > MAX_LICENSE_BYTES) {
      return setError(`License file is too large (${Math.round(file.size / 1024)} KB). Max 5 KB — please upload a smaller file.`);
    }

    const reader = new FileReader();
    reader.onload = () => setLicenseData(String(reader.result || ''));
    reader.onerror = () => setError('Could not read that file. Try another license.');
    reader.readAsText(file);
  };

  const save = async () => {
    setError('');
    setMsg('');

    try {
      const API_BASE = window.MANTLE_CONFIG.API_URL ?? 'http://localhost:5000/api';
      const token = localStorage.getItem('jwt');
      setBusy(true);
      // post file content to API
      const resp = await axios.post(`${API_BASE}/licenses/upload`, { license: licenseData }, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': window.MANTLE_CONFIG.TENANT_DB
        },
      });

      if ((resp.data?.status ?? "Failure") === "Success") {
        setMsg('Saved.');
        setTimeout(onClose, 750);
      } else {
        setError("Failed to upload and/or validate license! " + (resp.data?.err ?? ""));
        setMsg('');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to upload license.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Upload License File</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

          <div className="pt-4">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
              License
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <input
                  type="file"
                  accept="text/plain"
                  disabled={!admin}
                  onChange={onLicenseFile}
                  className="block w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 dark:file:border-slate-600 file:text-xs file:bg-white dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-200 hover:file:bg-slate-50 disabled:opacity-60"
                />
                {licenseData && admin && (
                  <button
                    type="button"
                    onClick={() => { setLicenseData(''); setError(''); }}
                    className="mt-1.5 text-xs text-red-500 hover:text-red-600"
                  >
                    Remove license
                  </button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              Upload an encrypted license file. Max 5 KB.
            </p>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
          {msg && <p className="text-sm text-green-600">{msg}</p>}
          {!admin && <p className="text-xs text-amber-600">You need an administrative role to change these.</p>}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !admin || !hasChanges}
            className={`px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 ${hasChanges ? 'bg-emerald-600 hover:bg-emerald-700 font-semibold' : 'bg-slate-900 hover:bg-slate-800'
              }`}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LicenseUploadModal;
